import { Link } from 'react-router-dom';
import { ArrowLeft, Heart } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          Back
        </Link>

        <div className="flex items-center gap-2 mb-8">
          <Heart className="h-6 w-6 text-primary" />
          <span className="text-lg font-bold">Apne<span className="text-primary">Doctors</span></span>
        </div>

        <h1 className="text-3xl font-bold text-foreground mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: February 2026</p>

        <div className="prose prose-sm dark:prose-invert max-w-none space-y-6 text-muted-foreground">

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">1. Who We Are</h2>
            <p>
              ApneDoctors ("we", "us") operates a telemedicine platform connecting patients with licensed
              medical professionals. We are committed to protecting your health data with the highest
              standards of security and transparency.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">2. Data We Collect</h2>
            <h3 className="font-medium text-foreground mt-3 mb-1">For Patients</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Identity: name, date of birth, gender, contact details</li>
              <li>Medical history: conditions, allergies, medications, surgical history</li>
              <li>Consultation content: video/audio recordings (with explicit consent), transcripts</li>
              <li>AI assessment inputs and outputs</li>
              <li>Usage data: login times, feature interactions</li>
            </ul>
            <h3 className="font-medium text-foreground mt-3 mb-1">For Doctors</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Professional identity: name, email, phone</li>
              <li>Credentials: licence number, specialisation, degrees</li>
              <li>Consultation records and clinical notes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">3. How We Use Your Data</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>To facilitate consultations between patients and doctors</li>
              <li>To operate AI clinical support tools</li>
              <li>To verify doctor credentials</li>
              <li>To generate medical summaries and reports</li>
              <li>To maintain audit logs for clinical safety and legal compliance</li>
              <li>To improve platform quality and safety</li>
            </ul>
            <p className="mt-2">
              We do <strong>not</strong> sell your health data. We do not use health data for advertising.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">4. Data Storage & Security</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Health data stored on Supabase (PostgreSQL) with row-level security</li>
              <li>Video/audio recordings stored on encrypted AWS S3 storage</li>
              <li>All data encrypted at rest and in transit (TLS 1.3+)</li>
              <li>Access controlled by role-based permissions</li>
              <li>Immutable audit logs maintained for all data access</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">5. Who Can Access Your Data</h2>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>You</strong> — always have full access to your own data</li>
              <li><strong>Your treating doctor</strong> — during and after a consultation</li>
              <li><strong>Platform administrators</strong> — for operational and compliance purposes only</li>
              <li><strong>No third parties</strong> — unless required by law or with your explicit consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">6. AI Processing</h2>
            <p>
              AI models process your health inputs to provide clinical support to doctors. AI outputs are
              never the final clinical decision — they assist licensed professionals. AI processing is
              disclosed at each relevant touchpoint and requires your explicit consent.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">7. Your Rights</h2>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Access:</strong> Request a copy of all data we hold about you</li>
              <li><strong>Correction:</strong> Update inaccurate information via your profile</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
              <li><strong>Consent withdrawal:</strong> Withdraw AI processing consent (affects service availability)</li>
              <li><strong>Portability:</strong> Request your health data in a portable format</li>
            </ul>
            <p className="mt-2">To exercise these rights, contact us via the platform or at privacy@apnedoctors.in.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">8. Data Retention</h2>
            <p>
              Medical records are retained for a minimum of 7 years from the date of last consultation,
              in compliance with Indian medical record retention requirements. You may request deletion
              of non-clinical data at any time.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">9. Cookies & Tracking</h2>
            <p>
              We use essential session cookies only. We do not use third-party advertising trackers.
              Analytics are anonymised and aggregated.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">10. Changes to This Policy</h2>
            <p>
              We will notify you of material changes via email. Continued use of the Platform after
              notification constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">Contact</h2>
            <p>
              Data protection queries:{' '}
              <span className="text-foreground">privacy@apnedoctors.in</span>
              <br />
              General:{' '}
              <Link to="/#contact" className="text-primary hover:underline">Contact form</Link>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
